/*
 * This is the entry point for regular site functionality.
 * For the signed in (My Pages) functionality, see MyPagesApp.jsx.
 */

 // polyfills (mainly for ie10-support 🙃)
 import 'whatwg-fetch';
 import 'ie-array-find-polyfill';
 import 'nodelist-foreach-polyfill';
 import 'classlist-polyfill';
 import 'svgxuse';

 import fc from 'fastclick';
 import datasetPoly from 'element-dataset';
 import customEventPoly from './Lararforsakringar/Utils/polyfillCustomEvent';
 import documentListLoader from './Lararforsakringar/Utils/documentListLoader';
 import fileInputHelper from './Lararforsakringar/Utils/fileInputHelper';
 import searchInputHelper from './Lararforsakringar/Utils/searchInputHelper';
 import picturefill from 'picturefill';

 // init polyfills
 if (!Element.prototype.matches) {
     Element.prototype.matches = Element.prototype.msMatchesSelector;
 }

 datasetPoly();
 customEventPoly();
 picturefill();

 fileInputHelper();
 searchInputHelper();

// libs
import React from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import styled from 'styled-components';
import {Provider} from 'react-redux';
import moment from 'moment';
import 'moment/locale/sv';

// vanilla components
import WithScriptsUnhideHandler from './Lararforsakringar/DomHandlers/WithScriptsUnhideHandler';
import ResponsiveHeroBackgroundHandler from './Lararforsakringar/DomHandlers/ResponsiveHeroBackgroundHandler';
import FooterMenuHandler from './Lararforsakringar/DomHandlers/FooterMenuHandler';
import FaqAccordionHandler from './Lararforsakringar/DomHandlers/FaqAccordionHandler';
import InsuranceAccordionHandler from './Lararforsakringar/DomHandlers/InsuranceAccordionHandler';
import LoadMoreContentHandler from './Lararforsakringar/DomHandlers/LoadMoreContentHandler';
import PageWarningBarHandler from './Lararforsakringar/DomHandlers/PageWarningBarHandler';
import MyPagesWarningBarHandler from './Lararforsakringar/DomHandlers/MyPagesWarningBarHandler';
import CookieWarningBarHandler from './Lararforsakringar/DomHandlers/CookieWarningBarHandler';
import StickyElementHandler from './Lararforsakringar/DomHandlers/StickyElementHandler';
import HomeInsuranceQuoteHandler from './Lararforsakringar/DomHandlers/HomeInsuranceQuoteHandler';
import QuickInsuranceQuoteHandler from './Lararforsakringar/DomHandlers/QuickInsuranceQuoteHandler';
import DogInsuranceQuoteHandler from './Lararforsakringar/DomHandlers/DogInsuranceQuoteHandler';
import CatInsuranceQuoteHandler from './Lararforsakringar/DomHandlers/CatInsuranceQuoteHandler';
import SmallAnimalInsuranceQuoteHandler from './Lararforsakringar/DomHandlers/SmallAnimalInsuranceQuoteHandler';
import ClassToggler from './Lararforsakringar/DomHandlers/ClassToggler';
import ClassSyncer from './Lararforsakringar/DomHandlers/ClassSyncer';
import BasicCoverageHandler from './Lararforsakringar/DomHandlers/BasicCoverageHandler';
import SelectContentHandler from './Lararforsakringar/DomHandlers/SelectContentHandler';
import AccessibleClickAreasHandler from './Lararforsakringar/DomHandlers/AccessibleClickAreasHandler';
import AccessibleToolTip from './Lararforsakringar/DomHandlers/AccessibleToolTipHandler';
import FaqCategoryScrollHandler from './Lararforsakringar/DomHandlers/FaqCategoryScrollHandler';

import InternalInsurancePage from './Lararforsakringar/Pages/InternalInsurancePages/InternalInsurancePage';

// react "pages"/"apps"
import InsuranceGuidePage from './Lararforsakringar/Pages/InsuranceGuidePage/InsuranceGuidePage';
import RetirementGuidePage from './Lararforsakringar/Pages/RetirementGuidePage/RetirementGuidePage';

import CarInsuranceCampaignPage from './Lararforsakringar/Pages/CarInsuranceCampaignPage/Components/CarInsuranceCampaignPage';
import SchoolDirectorsCampaignPage from './Lararforsakringar/Pages/SchoolDirectorsCampaignPage/Components/SchoolDirectorsCampaignPage';
import CottageCampaignPage from './Lararforsakringar/Pages/CottageCampaignPage/Components/CottageCampaignPage';
import EmployerBrandingPage from './Lararforsakringar/Pages/EmployerBrandingPage/Components/EmployerBrandingPage';

// react features

import TwitterBlock from './Lararforsakringar/Features/SocialBlock/Components/TwitterBlock';
import LinkedinBlock from './Lararforsakringar/Features/SocialBlock/Components/LinkedinBlock';
import FacebookBlock from './Lararforsakringar/Features/SocialBlock/Components/FacebookBlock';
import InstagramBlock from './Lararforsakringar/Features/SocialBlock/Components/InstagramBlock';
import NewsBlock from './Lararforsakringar/Features/ListingTeaserBlocks/Components/NewsBlock';
import CalendarBlock from './Lararforsakringar/Features/ListingTeaserBlocks/Components/CalendarBlock';
import BlogBlock from './Lararforsakringar/Features/ListingTeaserBlocks/Components/BlogBlock';

import InsuranceHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/InsuranceHeroAnimation';
import RetirementHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/RetirementHeroAnimation';
import CustomerServiceHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/CustomerServiceHeroAnimation';
import BlogHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/BlogHeroAnimation';
import ManagedPensionAnimation from './Lararforsakringar/Features/AnimatedHeros/ManagedPensionAnimation';
import SeasonalGiftHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/SeasonalGiftHeroAnimation';
import SeasonalSnowGlobeHeroAnimation from './Lararforsakringar/Features/AnimatedHeros/SeasonalSnowGlobeHeroAnimation';

import ShortCutMenu from './Lararforsakringar/Features/ShortCutMenuBlock/Components/ShortCutMenuBlock';

import AnimatedUspTeaserCustomerSurvey from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserCustomerSurvey';
import AnimatedUspTeaserSchoolDirectorsEnvelope from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserSchoolDirectorsEnvelope';
import AnimatedUspTeaserSchoolDirectorsHeadset from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserSchoolDirectorsHeadset';
import AnimatedUspTeaserManagedPension from './Lararforsakringar/Features/AnimatedUspTeaser/Components/AnimatedUspTeaserManagedPension';

import VideoBlock from './Lararforsakringar/Features/VideoBlock/Components/VideoBlock';

import InsurelyForm from './Lararforsakringar/Features/InsurelyForm/InsurelyForm';

import CategorizedFAQQuestionBlock from './Lararforsakringar/Features/CategorizedFAQQuestionBlock/Components/CategorizedFAQQuestionBlock';

// store
import {configureStore} from './Lararforsakringar/State/siteStore';



// react mount routes
const sections = [
    { id: 'insurance-guide-root', component: <InsuranceGuidePage mountRootId="insurance-guide-root" /> },
    { id: 'retirement-guide-root', component: <RetirementGuidePage mountRootId="retirement-guide-root" /> },
    { class: 'twitter-block-react-app-root', component: TwitterBlock }, 
    { class: 'linkedin-block-react-app-root', component: LinkedinBlock },
    { class: 'facebook-block-react-app-root', component: FacebookBlock },
    { class: 'instagram-block-react-app-root', component: InstagramBlock },
    { class: 'news-block-react-app-root', component: NewsBlock },
    { class: 'calendar-block-react-app-root', component: CalendarBlock },
    { class: 'blog-block-react-app-root', component: BlogBlock },
    { class: 'video-block-react-app-root', component: VideoBlock },
    { id: 'insurance-animated-hero-react-app-root', component: <InsuranceHeroAnimation mountRootId="insurance-animated-hero-react-app-root" /> },
    { id: 'retirement-animated-hero-react-app-root', component: <RetirementHeroAnimation mountRootId="retirement-animated-hero-react-app-root" /> },
    { id: 'seasonal-snowglobe-animated-hero-react-app-root', component: <SeasonalSnowGlobeHeroAnimation mountRootId="seasonal-snowglobe-animated-hero-react-app-root" /> },
    { id: 'seasonal-gift-animated-hero-react-app-root', component: <SeasonalGiftHeroAnimation mountRootId="seasonal-gift-animated-hero-react-app-root" /> },
    { id: 'blog-animated-hero-react-app-root', component: <BlogHeroAnimation mountRootId="blog-animated-hero-react-app-root" /> },
    { id: 'customer-service-animated-hero-react-app-root', component: <CustomerServiceHeroAnimation mountRootId="customer-service-animated-hero-react-app-root" /> },
    { id: 'shortcut-menu-block-react-app-root', component: <ShortCutMenu mountRootId="shortcut-menu-block-react-app-root" /> },
    { id: 'blog-animated-hero-react-app-root', component: <BlogHeroAnimation mountRootId="blog-animated-hero-react-app-root" /> },
    { id: 'animated-usp-teaser-customer-survey-react-app-root', component: <AnimatedUspTeaserCustomerSurvey mountRootId="animated-usp-teaser-customer-survey-react-app-root" /> },
    { id: 'animated-usp-teaser-school-directors-envelope-react-app-root', component: <AnimatedUspTeaserSchoolDirectorsEnvelope mountRootId="animated-usp-teaser-school-directors-envelope-react-app-root" /> },
    { id: 'animated-usp-teaser-school-directors-headset-react-app-root', component: <AnimatedUspTeaserSchoolDirectorsHeadset mountRootId="animated-usp-teaser-school-directors-headset-react-app-root" /> },
    { id: 'car-insurance-campaign-page-react-app-root', component: <CarInsuranceCampaignPage mountRootId="car-insurance-campaign-page-react-app-root" /> },
    { id: 'school-directors-campaign-page-react-app-root', component: <SchoolDirectorsCampaignPage mountRootId="school-directors-campaign-page-react-app-root" /> },
    { class: 'animated-usp-teaser-managed-pension-react-app-root', component: AnimatedUspTeaserManagedPension },
    { id: 'managed-pension-animated-hero-react-app-root', component: <ManagedPensionAnimation mountRootId="managed-pension-animated-hero-react-app-root" /> },
    { id: 'pension-data-aggregation-form-react-app-root', component: <InsurelyForm mountRootId="pension-data-aggregation-form-react-app-root" /> },
    { id: 'cottage-campaign-page-react-app-root', component: <CottageCampaignPage mountRootId="cottage-campaign-page-react-app-root" /> },
    { id: 'employer-branding-page-react-app-root', component: <EmployerBrandingPage mountRootId="employer-branding-page-react-app-root" /> },
    { id: 'categorized-faq-question-block-react-app-root', component: <CategorizedFAQQuestionBlock mountRootId="categorized-faq-question-block-react-app-root" /> }
];

const AppWrapper = styled.div`
    position: relative;
    padding: 0;
`;

function App() {
    // debug   
    //window._bazDebug = true; 
    window._bazDebug = window.__lfSettings.jsDebugEnabled;

    // set moment locale
    moment.locale('sv');

    // fix touch delay on touch devices
    fc.FastClick.attach(document.body);

    // init handlers
    new WithScriptsUnhideHandler();
    new ResponsiveHeroBackgroundHandler();
    new FooterMenuHandler();
    new FaqAccordionHandler();
    new InsuranceAccordionHandler();
    new LoadMoreContentHandler();
    new PageWarningBarHandler();
    new MyPagesWarningBarHandler();
    new CookieWarningBarHandler();
    new StickyElementHandler();
    new HomeInsuranceQuoteHandler();
    new QuickInsuranceQuoteHandler();
    new DogInsuranceQuoteHandler();
    new CatInsuranceQuoteHandler();
    new SmallAnimalInsuranceQuoteHandler();
    new ClassToggler();
    new ClassSyncer();
    new SelectContentHandler();
    new BasicCoverageHandler();
    new InternalInsurancePage();
    new AccessibleClickAreasHandler();
    new AccessibleToolTip();
    new FaqCategoryScrollHandler();

    documentListLoader();

    // redux store
    const store = configureStore();

    // init react sections
    // init my pages react "app"
    sections.map((section) => {
        if (section.id) {
            let wrapperEl = document.getElementById(section.id);
            if (wrapperEl) {
                const root = createRoot(wrapperEl)
                root.render(
                    <Provider store={store}>
                        <AppWrapper>
                            {section.component}
                        </AppWrapper>
                    </Provider>
                );
            }
        } else if (section.class) {
            const wrapperEls = document.querySelectorAll('.' + section.class);
            wrapperEls.forEach(element => {
                const root = createRoot(element)
                root.render(
                    <Provider store={store}>
                        <AppWrapper>
                            <section.component element={element} />
                        </AppWrapper>
                    </Provider>
                );
            })
        }
    });
}

document.addEventListener('DOMContentLoaded', App);
